import { EsponenteDto, SoggettiPraticaDto, SoggettoDto, TitolareDto } from "@api/client";
import { companyDetailsSelector, companyIdCompanySelector } from "common/_redux/company/selectors";
import {
	subjectAddSubject,
	subjectGetDetails,
	subjectRemoveSubject,
	subjectSearchSubject,
	subjectSetTitolariEffettiviAdded,
	subjectUpdateSubject,
} from "common/_redux/subject/actions";
import {
	elencoTitolariEffettiviSelector,
	legaleRappresentanteSelector,
	titolariEffettiviAddedSelector,
} from "common/_redux/subject/selectors";
import BftButton from "common/components/button";
import ConfirmationModal from "common/components/confirmation-modal";
import BftRadioGroup from "common/components/radio-button";
import { getStatusTitolare, getTitolareStatusClass } from "common/helpers/common-utils";
import { TipoInserimento, TipoTitolare } from "constants/global";
import { titolareEffettivoOptions } from "constants/options";
import { AppRouterService } from "core/routes/app-router.service";
import { PathEnum } from "core/routes/path.enum";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { Esponente, Soggetto } from "models/subject";
import { useEffect, useState } from "react";
import { Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AggiungiTitolareModal from "./aggiungi-titolare-modal";

const title = "3. Dati titolare effettivo";
const subtitleBig = "Quali sono gli altri titolari effettivi dell'impresa?";
const subtitleSmall =
	"Esistono altre persone che, come te, detengono una partecipazione di più del 25% del capitale o dei diritti di voto o sono titolari di poteri di amministrazione/direzione dell'impresa? ";
const subtitleSmall2 = "Selezionali nell'elenco o aggiungili manualmente.";
const { TITOLARE_UNICO, TITOLARE, NON_TITOLARE, NON_TITOLARE_PF, VOID } = TipoTitolare;

const SceltaTitolareEffettivo: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// SELECTORS
	const idCompany = useAppSelector(companyIdCompanySelector);
	const elencoTitolariEffettivi = useAppSelector(elencoTitolariEffettiviSelector);
	const titolariEffettiviStored = useAppSelector(titolariEffettiviAddedSelector);
	const companyDetails = useAppSelector(companyDetailsSelector);

	// USESTATES
	const [legaleRappresentante, setLegaleRappresentante] = useState<SoggettoDto | undefined>(
		useAppSelector(legaleRappresentanteSelector)
	);
	const [titolariEffettiviList, setTitolariEffettiviList] = useState<Array<Esponente | Soggetto>>(
		[]
	);

	const [showAggiungiTitolareModal, setShowAggiungiTitolareModal] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [messageConfirmationModal, setMessageConfirmationModal] = useState("");

	const [tipoTitolare, setTipoTitolare] = useState<TipoTitolare>(VOID);
	const [tipoTitolareCerved, setTipoTitolareCerved] = useState<TipoTitolare>(VOID);

	useEffect(() => {
		if (!titolariEffettiviStored) {
			searchSubjects();
		} else {
			setTitolariEffettiviList(titolariEffettiviStored.list);
		}
	}, []);

	useEffect(() => {
		const tipoDocumento = elencoTitolariEffettivi?.some((e) => e.numeroDocumento);

		const handlePopState = () => {
			window.location.reload();
		};

		if (!tipoDocumento) {
			window.addEventListener("popstate", handlePopState);
		}
	}, []);

	// filtro i titolari di cerved per non far comparire il legale rappresentante
	useEffect(() => {
		if (legaleRappresentante && !tipoTitolare) setDichiarazioneDLGS(legaleRappresentante);
		if (legaleRappresentante?.codiceFiscale) {
			const filteredList = titolariEffettiviList.filter(
				(item) => item.codiceFiscale !== legaleRappresentante.codiceFiscale
			);
			if (JSON.stringify(filteredList) !== JSON.stringify(titolariEffettiviList)) {
				setTitolariEffettiviList(filteredList);
			}
		}
		dispatch(
			subjectSetTitolariEffettiviAdded({
				list: titolariEffettiviList,
			})
		);
	}, [legaleRappresentante, titolariEffettiviList]);

	const goBack = () => {
		navigate(-1);
	};

	const searchSubjects = () => {
		if (companyDetails?.infoAzienda?.tipoCliente != "PersonaFisica")
			dispatch(subjectGetDetails())
				.unwrap()
				.then((subjectRes) => {
					if (subjectRes) {
						setDichiarazioneDLGS(subjectRes.legaleRapp);

						if (subjectRes.elencoTitolariEffettivi?.length) {
							const esponentiMapped = subjectRes.elencoTitolariEffettivi?.map(
								(item: SoggettoDto) => ({
									...item,
									idFE: item.id,
									fulfilled: item.fullfiled,
									rejected: false,
								})
							);
							setTitolariEffettiviList(esponentiMapped as Soggetto[]);
						}
					}
				});

		// faccio la searchSubject solo se ho inserito un'azienda da Cerved e non sto tornando indietro da step successivi
		if (idCompany !== TipoInserimento.MANUALE && companyDetails?.idSoggetto) {
			dispatch(subjectSearchSubject(true))
				.unwrap()
				.then((res) => {
					if (res?.esponenti && res.esponenti.length > 0) {
						const esponentiMapped = res.esponenti.map((item: EsponenteDto) => ({
							...item,
							idFE: item.idEsponente,
							fulfilled: false,
							invitationRequest: false,
							rejected: false,
						}));
						setTitolariEffettiviList((prevList) => {
							// Filtro gli elementi dal searchSubject che sono già presenti nella subjects
							const uniqueEsponenti = esponentiMapped.filter(
								(item) =>
									!prevList.some(
										(prevItem) => prevItem.nome === item.nome && prevItem.cognome === item.cognome
									)
							);
							return prevList.concat(uniqueEsponenti);
						});
						setTipoTitolare((res.tipoTitolare as TipoTitolare) ?? VOID);
						setTipoTitolareCerved((res.tipoTitolare as TipoTitolare) ?? VOID);
					}
				});
		}
	};

	const setDichiarazioneDLGS = (legaleRapp: SoggettoDto | undefined) => {
		setLegaleRappresentante(legaleRapp);
		if (legaleRapp?.id === TipoInserimento.MANUALE) {
			setTipoTitolare(TipoTitolare.TITOLARE_UNICO);
		} else {
			setTipoTitolare((legaleRapp?.dichiarazioneDLGS as TipoTitolare) || VOID);
		}
		setTipoTitolareCerved((legaleRapp?.dichiarazioneDLGS as TipoTitolare) || VOID);
	};

	const navigateToNextStep = (titolare: Esponente) => {
		if (legaleRappresentante?.dichiarazioneDLGS === tipoTitolare) {
			checkSubjectAndNavigate(titolare);
		} else {
			const subject = {
				...legaleRappresentante,
				dichiarazioneDLGS: tipoTitolare,
			};

			dispatch(subjectUpdateSubject({ subject }))
				.unwrap()
				.then(() => {
					checkSubjectAndNavigate(titolare);
				});
		}
	};

	const handleClick = () => {
		const subject = {
			...legaleRappresentante,
			dichiarazioneDLGS: tipoTitolare,
		};

		dispatch(subjectUpdateSubject({ subject }));
		if (tipoTitolare === TITOLARE_UNICO) {
			dispatch(
				subjectSetTitolariEffettiviAdded({
					list: [],
				})
			);
		}

		navigate(AppRouterService.get(PathEnum.step4));
	};

	const checkSubjectAndNavigate = (titolare: Esponente) => {
		// controllo se il soggetto selezionato era già statto aggiunto con la addSubject, se sì non lo aggiungo nuovamente
		// Posso matcharli solo con nome e cognome perché i nuovi titolari aggiunti manualmente non hanno l'id
		const isSubjectAlreadyAdded = elencoTitolariEffettivi?.find(
			(el) => el.nome === titolare.nome && el.cognome === titolare.cognome
		);
		if (isSubjectAlreadyAdded) {
			getSubjectAndNavigate(titolare);
		} else {
			addSubjectAndNavigate(titolare);
		}
	};

	const addSubjectAndNavigate = async (titolare: Esponente) => {
		const titolareData: TitolareDto = {
			nome: titolare.nome,
			cognome: titolare.cognome,
			idSoggetto: titolare.idEsponente,
		};

		dispatch(subjectAddSubject([titolareData]))
			.unwrap()
			.then((res) => {
				getSubjectAndNavigate(titolare, res);
			});
	};

	const getSubjectAndNavigate = async (
		titolare: Esponente,
		soggettiPraticaJustAdded?: SoggettiPraticaDto | undefined
	) => {
		dispatch(subjectGetDetails())
			.unwrap()
			.then((subjectRes) => {
				if (subjectRes) {
					let currentTitolareId;
					// se sono passato dalla addSubject entro qui, altrimenti mi pesco il titolare dall'elenco
					if (soggettiPraticaJustAdded) {
						currentTitolareId = soggettiPraticaJustAdded.elencoTitolariEffettivi?.[0].id;
					} else {
						const titolareSelected = elencoTitolariEffettivi?.find(
							(el) => el.nome === titolare.nome && el.cognome === titolare.cognome
						);
						currentTitolareId = titolareSelected?.id;
					}
					dispatch(
						subjectSetTitolariEffettiviAdded({
							list: titolariEffettiviList,
							currentTitolareId,
						})
					);

					navigate(AppRouterService.get(PathEnum.identificazioneTitolareEffettivo), {
						state: { company: soggettiPraticaJustAdded },
					});
				}
			})
			.catch((err) => {
				// console.log(err);
			});
	};

	const isProceedEnabled = () => {
		if (tipoTitolare === TITOLARE_UNICO) {
			return true;
		}
		if (titolariEffettiviList.length === 0) {
			return false;
		}
		return titolariEffettiviList.every((el: Esponente | Soggetto) => {
			return el.fulfilled || el.invitationRequest || el.rejected;
		});
	};

	const isAddTitolareEnabled = () => {
		return [NON_TITOLARE, NON_TITOLARE_PF].includes(tipoTitolare)
			? titolariEffettiviList?.length < 4
			: titolariEffettiviList?.length < 3;
	};

	// aggiungi titloari
	const addTitolare = (item: Esponente) => {
		setShowAggiungiTitolareModal(false);
		if (item) {
			const newTitolariEffettiviList = [...titolariEffettiviList, item];
			setTitolariEffettiviList(newTitolariEffettiviList);
		}
	};

	// cancella titolari inseriti manualmente
	const removeTitolare = (titolare: Esponente) => {
		const filteredList = titolariEffettiviList.filter((item) => item.idFE !== titolare.idFE);
		setTitolariEffettiviList(filteredList);

		if (titolare.id) {
			if (elencoTitolariEffettivi?.find((e) => e.id === titolare.id))
				dispatch(
					subjectRemoveSubject({
						subject: {
							id: titolare.id,
							tipoSoggetto: "Titolare",
						},
					})
				).then(() => {
					dispatch(subjectGetDetails());
				});
		} else {
			rejectTitolare(titolare);
		}
	};

	// dichiarazione D.L.GS.
	const changeTipoTitolare = (value: TipoTitolare) => {
		setTipoTitolare(value);
		if (tipoTitolareCerved !== VOID && value !== tipoTitolareCerved) {
			let result = "";
			switch (tipoTitolareCerved) {
				case TITOLARE_UNICO:
					result = "risulti essere uno dei titolari effettivi";
					break;
				case TITOLARE:
					result = "risulti essere uno dei titolari effettivi";
					break;
				case NON_TITOLARE:
					result = "non risulti essere uno dei titolari effettivi";
					break;
				default:
					break;
			}
			const message = `Dai nostri controlli ${result}, ma hai effettuato una scelta differente nella dichiarazione ai sensi del D.L.GS N.231/2007. Confermi?`;
			setMessageConfirmationModal(message);
			setShowConfirmationModal(true);
		}
	};

	// click su "Non è più titolare"
	const rejectTitolare = (titolare: Esponente | Soggetto) => {
		setTitolariEffettiviList((prevState) => {
			const updatedList = setRejectField(prevState, titolare);

			const allRejected = updatedList.every((titolare) => titolare.rejected);

			if (allRejected) {
				setTipoTitolare(TipoTitolare.TITOLARE_UNICO);
			}

			setTitolariEffettiviList(updatedList);
			dispatch(
				subjectSetTitolariEffettiviAdded({
					list: updatedList,
				})
			);

			return updatedList;
		});
	};

	const setRejectField = (
		prevState: Array<Esponente | Soggetto>,
		titolare: Esponente | Soggetto
	) => {
		return prevState.map((el) => {
			if (el.idFE === titolare.idFE) {
				return { ...el, rejected: true };
			}
			return el;
		});
	};

	const isTitolareCompleted = (titolare: Esponente | Soggetto): boolean => {
		return titolariEffettiviList.some((el: Esponente | Soggetto) => {
			return (
				el.nome === titolare.nome &&
				el.cognome === titolare.cognome &&
				(el.fulfilled || el.invitationRequest || el.rejected)
			);
		});
	};

	useEffect(() => {
		if (companyDetails?.infoAzienda?.tipoCliente === "PersonaFisica")
			setTipoTitolare(TipoTitolare.TITOLARE_UNICO);
	}, [titolariEffettiviList]);

	const hasFulfilled = titolariEffettiviList.some((i) => i.fulfilled === true);

	return (
		<>
			<h2 className="mb-3 ms-3">{title}</h2>

			<Card className="shadow">
				<Card.Body>
					<h4 className="ms-2">Sei un titolare effettivo dell'impresa?</h4>
					{hasFulfilled ? (
						<BftRadioGroup
							controlId="titolareEffettivo"
							label="Il legale rappresentante dichiara che, ai sensi del D.L.GS.N.231/2007:*"
							options={titolareEffettivoOptions}
							onChange={(e: any) => changeTipoTitolare(e.target.value)}
							selectedOption={tipoTitolare}
							disabled
						/>
					) : (
						<BftRadioGroup
							controlId="titolareEffettivo"
							label="Il legale rappresentante dichiara che, ai sensi del D.L.GS.N.231/2007:*"
							options={titolareEffettivoOptions}
							onChange={(e: any) => changeTipoTitolare(e.target.value)}
							selectedOption={tipoTitolare}
						/>
					)}
				</Card.Body>
			</Card>

			{tipoTitolare !== TITOLARE_UNICO && (
				<>
					<h4 className="mb-3 ms-3 mt-4">{subtitleBig}</h4>
					<span className="gray">
						{subtitleSmall}
						<br />
						{subtitleSmall2}
					</span>

					{titolariEffettiviList?.map((item: any, index: number) => (
						<div key={item.idFE}>
							<Card className="shadow">
								<Card.Body>
									<div className="d-flex align-items-center">
										<div>
											<div
												className={`square-icon-shadow icon-card-v2${getTitolareStatusClass(item)}`}
											>
												<div className="square-icon-container">
													<i className={`fas fa-user square-icon`}></i>
												</div>
											</div>
										</div>
										<div>
											<h6 className="m-0 ms-3">
												{item.nome} {item.cognome}
											</h6>
											<span className="gray small">{getStatusTitolare(item)}</span>
										</div>
										<div className="d-flex align-items-center text-center ms-auto">
											{!isTitolareCompleted(item) && (
												<>
													<OverlayTrigger
														key={`status-tooltip-rejcet${item.idFE}`}
														overlay={
															<Tooltip
																id={`tooltip-rejcet${item.idFE}`}
															>{`Non è più titolare`}</Tooltip>
														}
													>
														<div className="me-3">
															<BftButton
																className="btn-icon"
																onClick={() => removeTitolare(item)}
																style={{
																	minHeight: "2rem",
																	width: "2rem",
																	background: "transparent !important",
																}}
															>
																<i className="fas fa-trash" style={{ color: "#e15656" }}></i>
															</BftButton>
														</div>
													</OverlayTrigger>
													<OverlayTrigger
														key={`status-tooltip-confirm${item.idFE}`}
														overlay={
															<Tooltip id={`tooltip-confirm${item.idFE}`}>{`Compila`}</Tooltip>
														}
													>
														<div>
															<BftButton
																className="btn-buffetti-secondary"
																onClick={() => navigateToNextStep(item)}
																style={{
																	minHeight: "2rem",
																	width: "2rem",
																	fontSize: "1rem",
																	padding: "0",
																	borderRadius: "0.375rem",
																	marginRight: "1rem",
																}}
															>
																<i className={`fas fa-pen`} style={{ fontSize: "small" }}></i>
															</BftButton>
														</div>
													</OverlayTrigger>
												</>
											)}
										</div>
									</div>
								</Card.Body>
							</Card>
						</div>
					))}
					<Card className="shadow">
						<Card.Body>
							<div className="d-flex align-items-center">
								<div className="col-11">
									<h6 className="m-0 ms-3">Non è presente in questa lista?</h6>
									<span className="gray small">
										Seleziona questa opzione se il nome non è presente nei nominativi dell'elenco.
									</span>
								</div>
								<div className="col-1 text-right">
									<BftButton
										className="btn-buffetti-secondary"
										isDisabled={!isAddTitolareEnabled()}
										onClick={() => setShowAggiungiTitolareModal(true)}
										style={{
											minHeight: "2rem",
											width: "2rem",
											fontSize: "1rem",
											padding: "0",
											borderRadius: "0.375rem",
											marginRight: "1rem",
										}}
									>
										+
									</BftButton>
								</div>
							</div>
						</Card.Body>
					</Card>
				</>
			)}
			<Row className="justify-content-between mx-3">
				<Col xs={1} className="p-0">
					<BftButton className="btn btn-buffetti-secondary ms-0 px-3" onClick={goBack}>
						<i className="fas fa-arrow-left"></i>
					</BftButton>
				</Col>
				<Col xs={10} md={11} className="pe-0">
					<BftButton
						className="btn-buffetti col-12"
						isDisabled={!isProceedEnabled()}
						onClick={handleClick}
					>
						Vai allo step successivo
					</BftButton>
				</Col>
			</Row>
			<AggiungiTitolareModal
				show={showAggiungiTitolareModal}
				onClose={(e: any) => addTitolare(e)}
			/>
			<ConfirmationModal
				show={showConfirmationModal}
				title="Attenzione"
				message={messageConfirmationModal}
				onClose={() => {
					setShowConfirmationModal(false);
					setTipoTitolare(tipoTitolareCerved);
				}}
				onConfirm={() => {
					setShowConfirmationModal(false);
				}}
			/>
		</>
	);
};

export default SceltaTitolareEffettivo;
